import { Input } from "@kovernow/react-ui/components/input";
import { Label } from "@kovernow/react-ui/components/label";
import { cn } from "@kovernow/react-ui/lib/utils";
import { useId, useState, type KeyboardEvent, type ReactNode } from "react";

export interface ComboboxOption {
  id: string;
  label: string;
  description?: string;
}

/**
 * ARIA 1.2 editable combobox with a list popup. The input stays visible and
 * carries `role=combobox`, `aria-expanded`, `aria-controls` and
 * `aria-activedescendant`. The caller filters (the server does, for item
 * search); this only renders and handles the keyboard.
 */
export function Combobox({
  label,
  value,
  onValueChange,
  options,
  onSelect,
  listLabel,
  hint,
  footer,
  testId,
  inputId,
  closeOnBlur = true,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  onSelect: (option: ComboboxOption) => void;
  /** Accessible name of the listbox. */
  listLabel: string;
  hint?: ReactNode;
  /** Under the list, outside the listbox (count, "Show more results"). */
  footer?: ReactNode;
  testId?: string;
  inputId?: string;
  /** false: the list stays shown after focus leaves (item search results). */
  closeOnBlur?: boolean;
}) {
  const uid = useId();
  const id = inputId ?? `${uid}-input`;
  const listId = `${uid}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const expanded = open && options.length > 0;
  const activeIndex = active >= 0 && active < options.length ? active : -1;
  const optionId = (i: number) => `${uid}-opt-${i}`;

  const choose = (o: ComboboxOption) => {
    setOpen(false);
    setActive(-1);
    onSelect(o);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (options.length ? (i + 1) % options.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
    } else if (e.key === "Enter") {
      if (expanded && activeIndex >= 0) {
        e.preventDefault();
        choose(options[activeIndex]!);
      }
    } else if (e.key === "Escape") {
      if (expanded) {
        e.preventDefault();
        setOpen(false);
        setActive(-1);
      }
    }
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <Label htmlFor={id}>{label}</Label>
      {hint}
      <Input
        id={id}
        role="combobox"
        type="text"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        className="min-h-11 border-input"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (closeOnBlur) setOpen(false);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
      />
      <ul
        id={listId}
        role="listbox"
        aria-label={listLabel}
        hidden={!expanded}
        className="max-h-80 overflow-y-auto rounded-md border bg-popover text-popover-foreground"
      >
        {options.map((o, i) => (
          <li
            key={o.id}
            id={optionId(i)}
            role="option"
            aria-selected={i === activeIndex}
            className={cn(
              "flex min-h-11 cursor-pointer flex-col justify-center px-3 py-2",
              i === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-muted",
            )}
            // Keep focus in the input (no blur before the click lands).
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(o)}
          >
            <span className="font-semibold">{o.label}</span>
            {o.description ? <span className="text-sm text-muted-foreground">{o.description}</span> : null}
          </li>
        ))}
      </ul>
      {footer}
    </div>
  );
}
